// src/components/common/BottomSheetMultiSelect.jsx
/**
 * BottomSheetMultiSelect — multi-select dropdown that adapts per platform.
 *
 * Mobile  : glass pill trigger (shows count when active) →
 *           SwipeableDrawer bottom sheet with checkbox list + Apply / Clear
 * Desktop : standard MUI Select multiple. The chosen values are drawn as
 *           TEXT — one line, comma-separated, ellipsis on overflow — and NOT
 *           as chips (E1a Folgerunde 9, 2026-10-08). A chip inside the field is
 *           ~26 px tall and pushed the control ~11 px above its neighbours in
 *           the same row (measured on `/products` and in Fulfillment). The
 *           one-chip rule (Regel 22 §Component rules 1) holds more strictly
 *           than before: the field now carries NO chip rather than a second
 *           kind. The pill lives on in the mobile sheet (`FilterSheet`), where
 *           it carries no field height.
 *           🔴 Das Etikett SCHWEBT (`InputLabel shrink` + `notched`, Hausform
 *           `VendorDetails.jsx:1072-1095`). Ohne das steht es bei LEERER Auswahl im
 *           Feld und liest sich als Wert — sichtbar nur an der Mehrfachauswahl,
 *           weil ein Einfach-Select daneben immer einen Wert hat und von selbst
 *           schrumpft (E1b, 2026-10-07). Probe:
 *           `BottomSheetMultiSelect.etikett.test.jsx`.
 *
 * 🔴 `desktopForm="pill"` (UI2 Teil 10, G0.4 / BS7) — der Facetten-Ausloeser,
 *    dieselbe Bauform wie in `BottomSheetSelect` (dort steht die ausfuehrliche
 *    Begruendung). 🔴 **Rein additiv:** die Vorgabe ist `'field'`, also
 *    Zeichen fuer Zeichen die heutige Form — kein bestehender Aufrufer
 *    uebergibt das Prop. 🔴 **Der Zweig ist DESKTOP-only** (Konzept §12 BS7:
 *    „Desktop-Zweig"; „mobil unverändert Blatt").
 *
 *    🔴 **Die Flaeche der Pille ist ein nicht-modaler `Popper`** (`Popper` +
 *    `Paper` + `ClickAwayListener`, KEIN MUI-`Popover`), kein `dialog` — sie
 *    traegt deshalb **kein `aria-modal`** (das gilt nur fuer
 *    `dialog`/`alertdialog`). Der Ausloeser sagt `aria-haspopup={disabled ? undefined : 'listbox'}` an,
 *    die Flaeche ist eine `MenuList` mit `role="listbox"` und
 *    `aria-multiselectable="true"`. **Fokus:** beim Oeffnen auf die erste
 *    GEWAEHLTE Option, sonst auf die erste (`autoFocusItem`); Pfeiltasten/
 *    `Home`/`End`/Typeahead liefert `MenuList`; `Tab` und `Esc` schliessen und
 *    geben den Fokus an den Ausloeser zurueck. **Schliessen:** `ClickAwayListener`
 *    um das `Paper`, Aussenklick auf den Ausloeser ausgenommen; der Ausloeser
 *    SCHALTET UM. Die lange Begruendung jeder Haelfte steht in
 *    `BottomSheetSelect`.
 *
 *    🔴 Auf dem TELEFON bleibt der Ausloeser Zeichen fuer Zeichen der heutige —
 *    nur in der Facettenform wird er ein Knopf. Seine `aria-haspopup`-Zusage
 *    ist dort **`"dialog"`** (die Flaeche IST das Blatt mit `role="dialog"`) und
 *    haengt am selben Waechter wie `role`/`tabIndex` (`alsKnopf`).
 *    🔴 Die Pille traegt `FACET_PILL_SX` (40 px ab `sm`), NICHT `GLASS_PILL_SX`
 *    (32 px) — sonst stuende sie 8 px zu tief neben einem `size="small"`-Feld
 *    derselben Zeile (N2.4, Plan §G0.4 F.4).
 *
 *    🔴 **`summarize` gehoert NUR hierher.** Ab drei Werten zeigt die Pille
 *    „3 selected" statt der kommagetrennten Etiketten (Plan §G0.4 F.5). Die
 *    Vorgabe ist **aus** (`undefined`) = die heutige Anzeige; „an" waere eine
 *    Aenderung an 6 bestehenden Aufrufern und damit nicht additiv (WV1 V3).
 *    🔴 **`clearable` ist hier NEU** (Vorgabe `false`); in `BottomSheetSelect`
 *    gibt es es seit jeher (`clearable = false`, „All"-Zeile). Es zeigt das ✕
 *    in der Pille und leert die Auswahl — die Blattform bleibt unveraendert.
 *
 *    🔴 **`closeOnSelect` ist hier NEU** (Vorgabe `false`, 2026-10-18; Karte
 *    `2026-10-14-facetten-popover-ueberlagert-z2-z3`). „An" heisst: die Pille
 *    schliesst nach **jeder** Auswahl und gibt den Fokus an den Ausloeser
 *    zurueck. Der Anlass ist eine **Ueberlagerung**, kein Geschmack: der
 *    `Popper` sitzt an der Pille in Z1 (`placement="bottom-start"`,
 *    `zIndex.modal`) und waechst nach unten ueber **Z2** und **Z3**. Solange er
 *    offen ist, ist der ✕ eines Z3-Chips unerreichbar und die aktive
 *    Einschraenkung unsichtbar. 🔴 Die Vorgabe bleibt die OFFENE Liste: drei
 *    der vier Bestandsflaechen (`/orders`, `/products`, Musterroute) haben
 *    keine Z3-Zeile unter der Pille, und das Schliessen kostete dort je Wert
 *    einen Aufklapp ohne Gegenwert. **Rein additiv** — kein Bestandsaufrufer
 *    uebergibt das Prop, der Baum bleibt dort Zeichen fuer Zeichen der alte.
 *
 *    🔴 **Der Grund der leeren Liste steht auch maschinell und ist angesagt
 *    (R16-W1, Wiedervorlage 3, 2026-10-12).** Die Zeile traegt `data-reason` in
 *    den Woertern der Hausform `EmptyState` (`ready` → `no-data`; `loading`
 *    kein Wort) und eine `id`, auf die die `listbox` per `aria-describedby`
 *    zeigt — die leere Liste ist nicht fokussierbar, ihr Grund waere sonst
 *    stumm. Vier Gruende, vier Saetze; `unknown` hat einen EIGENEN („Options
 *    not loaded yet"), keinen Rueckfall auf `ready` (Konzept §8.1 / N8.3,
 *    R16-C1). Die Tabelle liegt in `leerText.js` (EINE Wahrheit, R15-W5).
 *
 *    🔴 **`disabled`: die Rolle bleibt, die Zusagen fallen (R16-W3, Wiedervorlage
 *    3).** In der Pille steht `role="button"` auch bei `disabled`
 *    (MUI-Konvention, `tabIndex={-1}`) — ohne sie haette `aria-disabled` keinen
 *    Traeger: ARIA 1.2 §6.5 kuendigt die globale Verwendung ab („Used in Roles"),
 *    getragen wird es von `button`; `aria-haspopup`/`aria-expanded` haengen am Waechter.
 *    ⚠️ **Der Mobilzweig ist NICHT angefasst:** dort traegt `alsKnopf`
 *    (`!disabled && desktopForm === 'pill'`) bereits `role`, `tabIndex`,
 *    `onKeyDown`, `aria-expanded` UND `aria-haspopup` am selben Waechter — und
 *    ein Verbraucher (`RMAOverviewLayout.jsx:593/612`) uebergibt dort
 *    `disabled`, eine Aenderung waere nicht additiv.
 *    ⚠️ **Und die Auswahl gibt hier NICHTS zurueck, mit Absicht:** ein Klick
 *    laesst die Liste OFFEN (`toggle`), der Fokus bleibt auf der Zeile — der
 *    Blocker der Einfachauswahl entsteht erst durch das Schliessen.
 *
 * Props:
 *   label      : string                      — label shown in pill + sheet header + desktop Select
 *   value      : string[]                    — controlled selected values
 *   onChange   : (value: string[]) => void
 *   options    : [{ value: string, label: string, count?: number }]
 *                `count` ist additiv (UI2 Teil 10, G11a): steht er da, zeigt die
 *                LISTE „label (count)"; der Ausloeser zeigt weiter nur `label`.
 *                Ohne `count` ist die Ausgabe zeichengleich zur heutigen.
 *   disabled   : bool
 *   sx         : object                      — extra sx on the trigger (both variants)
 *   desktopForm: 'field' | 'pill'            — Vorgabe `'field'` = heutiges MUI-Select.
 *                                              `'pill'` = Facetten-Pille (G0.4, opt-in)
 *   summarize  : bool                        — ab 3 Werten „3 selected" (Vorgabe aus)
 *   clearable  : bool                        — ✕ in der Pille, leert die Auswahl (Vorgabe false)
 *   optionsStatus: 'ready' | 'loading' | 'error' | 'unknown' — warum die
 *                                              Optionsliste leer ist; nur die
 *                                              PILLE liest es (Vorgabe `'ready'`).
 *                                              Ein Wert ausserhalb der Menge
 *                                              WIRFT (Tabelle in `leerText.js`)
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Typography,
  SwipeableDrawer,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Stack,
  IconButton,
  Button,
  Divider,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  MenuList,
  OutlinedInput,
  Paper,
  Popper,
  ClickAwayListener,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  GLASS_PILL_SX,
  FACET_PILL_SX,
  FACET_PILL_MAX_WIDTH_PX,
  BOTTOM_SHEET_TRANSITION,
  LISTBOX_MAX_HEIGHT_PX,
  TOUCH_TARGET_MIN,
  FS,
  SP,
} from '../../theme/tokens';
// 🔴 R16-C1 (Wiedervorlage 3, 2026-10-12): dieselbe Gruende-Tabelle wie in
// `BottomSheetSelect` — sie liegt jetzt EINMAL in `leerText.js` (R15-W5).
// Der Grund der leeren Liste kommt von AUSSEN; der Baustein kann ihn nicht wissen.
import { leerTextFuer, grundImDom } from './leerText';

/**
 * Optionstext fuer die LISTE — der Zaehler steht in der Zeile, nicht im
 * Ausloeser (UI2 Teil 10, G11a / §2.3). Ohne `count` ist die Ausgabe Zeichen
 * fuer Zeichen die heutige. Der Ausloeser liest weiterhin `opt.label`.
 */
const optionText = (opt) => (typeof opt.count === 'number' ? `${opt.label} (${opt.count})` : opt.label);

export default function BottomSheetMultiSelect({
  label,
  value = [],
  onChange,
  options = [],
  disabled = false,
  sx = {},
  // 🔴 G0.4: `'field'` = die heutige Form (Vorgabe). `'pill'` = die
  // Facetten-Pille; in G0 von NIEMANDEM aufgerufen.
  desktopForm = 'field',
  // Ab 3 Werten „3 selected". Vorgabe aus = die heutige Anzeige (WV1 V3).
  summarize,
  // NEU hier (in `BottomSheetSelect` gibt es es seit jeher). Vorgabe `false`.
  clearable = false,
  // 🔴 R16-W2 (Regel 22 §5): der Grund der leeren Liste kommt von AUSSEN —
  // „es gibt nichts" waere unter `error`/`unknown` die falsche Aussage. Nur die
  // PILLE liest es; die Vorgabe `'ready'` aendert nichts.
  optionsStatus = 'ready',
  // 🔴 NEU (2026-10-18, Karte `2026-10-14-facetten-popover-ueberlagert-z2-z3`):
  // die Pille schliesst nach JEDER Auswahl. Rein ADDITIV — die Vorgabe `false`
  // ist Zeichen fuer Zeichen die heutige Form (die Liste bleibt offen, s.
  // Begruendung an der `MenuList`), und kein Bestandsaufrufer uebergibt das Prop.
  closeOnSelect = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  // 🔴 Der Name des Desktop-Feldes. `InputLabel` und `Select` sind in MUI NICHT
  // von selbst verbunden — ohne `labelId` bleibt das Etikett ohne Bezug und
  // `getByLabelText('Status')` findet nur den Text, kein Bedienelement.
  //
  // 🔴 Nur `labelId`, KEIN `id` am `Select`: MUI setzt dann
  // `aria-labelledby="<labelId> <id>"` (`SelectInput.js:461`), und der
  // zugängliche Name zöge die angezeigten Werte mit ein („Status active").
  // Nur `labelId` ⇒ der Name ist genau die Beschriftung.
  const selectLabelId = useId();
  // Der Anker des Popovers der Facetten-Pille (`desktopForm="pill"`).
  const anker = useRef(null);
  // Die Liste, auf die der Ausloeser per `aria-controls` zeigt (R16-W7).
  const listenId = useId();
  // 🔴 R16-W1 (Wiedervorlage 3): die Leerzeile traegt eine Kennung, damit die
  // `listbox` sie per `aria-describedby` als BESCHREIBUNG fuehren kann. Ohne
  // sie stuende der Grund nur gedruckt da: die leere Liste ist nicht
  // fokussierbar (`autoFocusItem` findet keine Zeile), ein Vorleser kaeme an
  // den Satz nie heran.
  // 🔴 ABGELEITET, kein zweiter `useId()`-Aufruf: ein zusaetzlicher `useId`
  // verschiebt die Zaehlung und damit JEDE danach erzeugte Kennung im Baum
  // (gemessen, A/B-Renderprobe). Die Zeile haengt an der Kennung der Liste.
  const leerId = `${listenId}-leer`;

  // 🔴 NICHT-MODAL heisst: Aussenklick und Esc schliessen, sonst nichts
  // (R16-W5). Dieselbe Loesung wie in `BottomSheetSelect` (dort steht die lange
  // Begruendung): der Aussenklick sitzt am `ClickAwayListener` um das `Paper`
  // (Bauform G0.5), hier bleibt nur `Esc` — die Flaeche ist nicht modal, ein
  // `Esc` erreicht sie also nie von selbst.
  //
  // 🔴 Der Effekt steht HIER und nicht im Pille-Zweig: der Zweig ist ein
  // vorzeitiger `return`, ein `useEffect` darin verletzte die Rules of Hooks.
  // Fuer jeden Bestandsaufrufer tut er NICHTS (`desktopForm` ist dort `'field'`).
  useEffect(() => {
    if (!open || isMobile || desktopForm !== 'pill') return undefined;
    const aufEscape = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      anker.current?.focus();
    };
    document.addEventListener('keydown', aufEscape);
    return () => document.removeEventListener('keydown', aufEscape);
  }, [open, isMobile, desktopForm]);

  // 🔴 R16-C1 (Wiedervorlage 3): der Grund ist eine GESCHLOSSENE Menge. Ein
  // unbekannter Wert WIRFT — und zwar HIER, fuer jeden Zweig, nicht erst beim
  // Lesen der leeren Liste: sonst bliebe ein Tippfehler in `optionsStatus` an
  // einer gefuellten Liste unsichtbar. Hausform `EmptyState.jsx:122-124`.
  // Die lange Begruendung (vierter Grund `unknown` mit eigenem Satz) steht in
  // `BottomSheetSelect` und in `leerText.js`.
  const leerSatz = leerTextFuer(optionsStatus);

  // ── Facetten-Pille (desktopForm="pill") ───────────────────────
  // 🔴 Plan §G0.4 F.1–F.5. Der Zweig feuert NUR auf dem DESKTOP und NUR, wenn
  // ein Aufrufer `desktopForm="pill"` uebergibt — in G0 tut das niemand.
  // 🔴 Auf dem TELEFON bleibt der Ausloeser unveraendert (Konzept §12 BS7:
  // „mobil unverändert Blatt"). Seine `aria-haspopup`-Zusage bleibt dort
  // `"dialog"` — die Flaeche IST das Blatt mit `role="dialog"`. Die erste
  // Fassung sagte dort `"listbox"` an und versprach damit eine Liste, die nicht
  // kommt (R16-W1 / R15-W4, 2026-10-12). S. Mobil-Zweig unten.
  if (!isMobile && desktopForm === 'pill') {
    const hasValue = value.length > 0;
    // 🔴 Der Ausloeser SCHALTET UM (Hausform MUI-`Select`, Vorbild G0.5
    // `FilterSheet.jsx`) — R15-Bedingung 3.
    const umschalten = () => setOpen((v) => !v);
    const schliesse = () => setOpen(false);
    const toggle = (val) => onChange(
      value.includes(val) ? value.filter((v) => v !== val) : [...value, val],
    );
    // 🔴 F.5: ab DREI Werten die Zahl statt der Etiketten. `summarize` ist die
    // Vorgabe `undefined` = heutige Anzeige.
    const anzeige = summarize && value.length >= 3
      ? `${value.length} selected`
      : value.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', ');
    const pilleText = hasValue ? `${label}: ${anzeige}` : label;
    // 🔴 R16-W4/R16-W2: leere Optionsliste → die Flaeche nennt den GRUND
    // (Konzept §11, Zeile „Abhängigkeiten": „zeigt den Grund, wenn sie leer
    // ist; nie still deaktiviert"; Regel 22 §5). Text statt
    // `DisabledReason`-Tooltip: dessen eigener `@dontUseFor` schliesst einen
    // DAUERHAFT deaktivierten Zustand aus, und eine deaktivierte Pille koennte
    // ihr Popover nicht mehr oeffnen — der Grund waere unerreichbar. Die Zeile
    // steht AUSSERHALB der `listbox` (R16-W1). Lange Begruendung in
    // `BottomSheetSelect`.
    const leerText = options.length === 0 ? leerSatz : null;
    // 🔴 R16-W1 (Wiedervorlage 3): der Grund steht auch MASCHINELL im DOM —
    // `data-reason` in den Woertern von `EmptyState` (`ready` → `no-data`; die
    // Lade-Lage traegt GAR KEIN `data-reason`). Pruefflaeche P9/P33 (`[data-reason]`).
    const leerGrund = options.length === 0 ? grundImDom(optionsStatus) : undefined;
    // 🔴 R16-Blocker 1: der Fokus geht beim Oeffnen auf die erste GEWAEHLTE
    // Option, sonst (Index −1) auf die erste ueber `autoFocusItem` der Liste.
    const gewaehltIndex = options.findIndex((o) => value.includes(o.value));

    // 🔴 R16-Blocker 1 / R15-Bedingung 3: Aussenklick schliesst, der Ausloeser
    // ist ausgenommen. Hausform `ShipmentStatusPopover.jsx:92-96`, Bauform G0.5.
    const beiAussenklick = (e) => {
      if (anker.current?.contains(e.target)) return;
      schliesse();
    };

    const pille = (
      <Box
        ref={anker}
        // 🔴 R16-W3 (Wiedervorlage 3, 2026-10-12): `role` bleibt bei `disabled`
        // STEHEN — MUI-Konvention fuer ein nicht-natives Bedienelement. Ohne die
        // Rolle haette `aria-disabled` keinen Traeger: ARIA 1.2 §6.5 kuendigt die
        // globale Verwendung ab, getragen wird es von `button` (Used in Roles).
        // `tabIndex={-1}` haelt ihn aus der Tab-Reihenfolge. Bis zur
        // Wiedervorlage 2 sagte `aria-haspopup` bei `disabled` eine Flaeche an,
        // deren Rolle fehlte (R15-W2 / R16-W4) — jetzt faellt die ZUSAGE, nicht
        // die Rolle.
        role="button"
        tabIndex={disabled ? -1 : 0}
        // 🔴 Die Pille oeffnet eine `listbox` und sagt genau das an. Auf dem
        // TELEFON traegt derselbe Ausloeser `"dialog"` (Blattform) —
        // R16-W1 / R15-W4, 2026-10-12.
        // 🔴 Und BEIDE Zusagen haengen am `disabled`-Waechter (R16-W3).
        aria-haspopup={disabled ? undefined : 'listbox'}
        aria-expanded={disabled ? undefined : open}
        // 🔴 R16-W7: Ausloeser und Liste sind zusaetzlich per ID verbunden.
        aria-controls={open ? listenId : undefined}
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : umschalten}
        onKeyDown={disabled ? undefined : (e) => {
          // 🔴 Nur der AUSLOESER selbst oeffnet — sonst oeffnete das Enter des
          // ✕ (eines echten Knopfes in ihm) zusaetzlich das Popover.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            umschalten();
          }
        }}
        sx={{
          ...FACET_PILL_SX,
          gap: SP.chip,
          pr: clearable && hasValue && !disabled ? SP.chip : undefined,
          color: hasValue ? 'primary.main' : 'text.primary',
          borderColor: hasValue ? 'primary.main' : 'var(--glass-border)',
          fontWeight: hasValue ? 600 : 500,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'default' : 'pointer',
          ...sx,
        }}
      >
        <Typography
          sx={{
            fontSize: FS.sm,
            fontWeight: 'inherit',
            lineHeight: 1,
            // 🔴 R16-W3: dieselbe Kappung wie in `BottomSheetSelect` — ohne sie
            // waechst die Pille unbegrenzt (N2.9: die Suche fuellt Z1, die
            // Facette steht rechts daneben). Der FELD-Zweig kuerzt laengst.
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: FACET_PILL_MAX_WIDTH_PX,
          }}
        >
          {pilleText}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: FS.sm, flexShrink: 0 }} />
        {/* 🔴 R16-W3 (Wiedervorlage 2): `disabled` schliesst das ✕ AUS — sonst
            traegt ein Element, das sich `aria-disabled` nennt, einen bedienbaren
            Knopf, der `onChange` ruft. */}
        {clearable && hasValue && !disabled && (
          // Hausform wie in `BottomSheetSelect` (Vorbild `SearchTrigger`):
          // echtes `IconButton` mit `aria-label` + `stopPropagation`.
          // 🔴 Und die zwei Haelften, die die erste Fassung ausliess:
          // (a) `TOUCH_TARGET_MIN` — mit `p: SP.chip` allein maß das ✕ rund
          //     20,5 px und lag unter der 24-px-Grenze (WCAG 2.5.8, R16-W2);
          // (b) die FOKUS-RUECKFUEHRUNG — der Knopf haengt an
          //     `clearable && hasValue`; mit dem Leeren verschwindet er und der
          //     Fokus fiele auf `document.body` (WCAG 2.4.3, R16-Blocker 2).
          //     `focus()` laeuft SYNCHRON, also vor dem Neuzeichnen.
          <IconButton
            size="small"
            aria-label={`Clear ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              anker.current?.focus();
              onChange([]);
            }}
            sx={{ ...TOUCH_TARGET_MIN, p: SP.chip, ml: SP.chip, color: 'text.secondary' }}
          >
            <CloseIcon sx={{ fontSize: FS.sm }} />
          </IconButton>
        )}
      </Box>
    );

    return (
      <>
        {pille}

        {/* 🔴 `Popper`, NICHT `Popover` — MUI's `Popover` sitzt auf `Modal`.
            Begruendung im Effekt oben. `role="presentation"` (R16-W5, in jsdom
            GEMESSEN): MUI's `Popper` traegt am Wurzelknoten die Vorgabe
            `role="tooltip"`; ohne diese Zeile saesse eine interaktive `listbox`
            in einem `tooltip`. Lange Begruendung in `BottomSheetSelect`.
            🔴 KEIN `disablePortal`: die Leiste traegt die Ueberlaufmaske aus
            G0.1 — eine ungeportete Flaeche wuerde daran abgeschnitten. */}
        <Popper
          open={open}
          anchorEl={anker.current}
          placement="bottom-start"
          role="presentation"
          sx={{ zIndex: (t) => t.zIndex.modal }}
        >
          <ClickAwayListener onClickAway={beiAussenklick}>
            <Paper
              sx={{ mt: SP.chip, minWidth: 200, maxHeight: LISTBOX_MAX_HEIGHT_PX, overflowY: 'auto' }}
            >
              {/* 🔴 Mehrfachauswahl: ein Klick schaltet um und laesst die Liste
                  OFFEN (MUI-`Select multiple`-Verhalten) — ein Facetten-Popover,
                  das sich nach dem ersten Wert schliesst, kostet je Wert einen
                  neuen Aufklapp.
                  🔴 **Das gilt fuer die VORGABE (`closeOnSelect={false}`) und
                  nicht mehr ausnahmslos** (2026-10-18, Karte
                  `2026-10-14-facetten-popover-ueberlagert-z2-z3`). Der
                  `Popper` haengt an der Pille in Z1 und waechst nach unten ueber
                  Z2 (Quick-Pillen) und Z3 (aktive Kriterien); bei offener Liste
                  ist der ✕ eines Z3-Chips unerreichbar und die aktive
                  Einschraenkung unsichtbar — auf `/rma` verbirgt die Vorgabe
                  „Resolved: hidden" 89 % der Faelle. Wer die Flaeche unter der
                  Liste BRAUCHT, uebergibt `closeOnSelect` und kauft die
                  Sichtbarkeit mit einem Aufklapp je Wert. Die Wahl liegt beim
                  Aufrufer, nicht im Baustein: die vier Bestandsflaechen haben
                  keine Z3-Zeile unter der Pille und behalten die offene Liste.
                  🔴 `MenuList`, nicht `List`: nur `MenuList` bringt die
                  Roving-Navigation, die `role="listbox"` laut ARIA-APG zusagt,
                  und holt per `autoFocusItem` den Fokus beim Oeffnen hinein
                  (R16-Blocker 1). */}
              <MenuList
                id={listenId}
                role="listbox"
                aria-label={label}
                aria-multiselectable="true"
                // 🔴 R16-W1 (Wiedervorlage 3): die leere Liste ist nicht
                // fokussierbar (`autoFocusItem` findet keine Zeile), ihr Grund
                // waere also stumm. Er haengt als BESCHREIBUNG an der Liste.
                aria-describedby={leerText ? leerId : undefined}
                autoFocusItem
                dense
                disablePadding
                onKeyDown={(e) => {
                  // 🔴 MUI-`Menu`-Verhalten (R16-Blocker 1): `Tab` schliesst die
                  // Flaeche und gibt den Fokus an den Ausloeser zurueck.
                  if (e.key !== 'Tab') return;
                  e.preventDefault();
                  schliesse();
                  anker.current?.focus();
                }}
              >
                {options.map((opt, i) => {
                  const checked = value.includes(opt.value);
                  return (
                    <MenuItem
                      key={opt.value}
                      role="option"
                      aria-selected={checked}
                      // 🔴 R16-Blocker 1: Fokus auf die erste GEWAEHLTE Option,
                      // sonst (Index −1) auf die erste ueber `autoFocusItem`.
                      autoFocus={i === gewaehltIndex}
                      onClick={() => {
                        toggle(opt.value)
                        // 🔴 `closeOnSelect` (2026-10-18): schliessen UND den
                        // Fokus zurueck auf den Ausloeser. Ohne die zweite
                        // Haelfte faellt er auf `<body>`, weil die Flaeche in
                        // einem Portal an `document.body` haengt und mit dem
                        // Schliessen ausgehaengt wird — dieselbe Fokus-Rueckgabe
                        // wie an `Esc` und `Tab` (`:400-401`), aus demselben
                        // Grund.
                        if (closeOnSelect) {
                          setOpen(false)
                          anker.current?.focus()
                        }
                      }}
                    >
                      {/* 🔴 R16-W6: das Kaestchen ist KEIN zweiter Tab-Stopp. Es
                          sitzt in einer `role="option"`, die die Auswahl schon
                          traegt (`aria-selected`) — ohne `tabIndex={-1}` bringt
                          jede Zeile einen zweiten Halt und eine zweite
                          Zustandsansage mit. */}
                      <Checkbox
                        checked={checked}
                        size="small"
                        disableRipple
                        tabIndex={-1}
                        sx={{ mr: 0.5, p: 0 }}
                      />
                      <ListItemText primary={optionText(opt)} primaryTypographyProps={{ variant: 'body2' }} />
                    </MenuItem>
                  );
                })}
              </MenuList>
              {/* 🔴 Der Leerzustand steht AUSSERHALB der `listbox` (R16-W1):
                  ARIA 1.2 laesst dort nur `option`/`group` zu. */}
              {leerText && (
                // 🔴 `id` (fuer `aria-describedby` der Liste) und `data-reason`
                // (die maschinelle Pruefflaeche, R16-W1) — beide in den Woertern
                // der Hausform `EmptyState`.
                <Box id={leerId} data-reason={leerGrund} sx={{ px: 2, py: 1 }}>
                  <Typography variant="body2" color="text.secondary">{leerText}</Typography>
                </Box>
              )}
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
    );
  }

  // ── Desktop: standard MUI Select multiple ─────────────────────
  if (!isMobile) {
    return (
      <FormControl size="small" disabled={disabled} sx={{ minWidth: 200, ...sx }}>
        {/* 🔴 `shrink` + `notched` (Hausform: `VendorDetails.jsx:1072-1095`).
            Ohne sie stand die Beschriftung bei LEERER Auswahl INNERHALB des
            Feldes — genau dort, wo sonst der Wert steht — und las sich als
            Wert statt als Etikett. Fotos (incoming-inventory Desktop): „zweites
            Select ‚Vendor' ohne Label (Platzhalter liest sich wie Wert)".
            Sichtbar wurde es nur am leeren Mehrfachauswahl-Feld: ein EINFACH-
            Select daneben hat immer einen Wert und schrumpft sein Etikett
            deshalb von selbst — die beiden standen in einer Reihe und sahen
            verschieden aus.
            `notched` schneidet die Lücke in den Rahmen; `displayEmpty` setzt es
            in MUI selbst (`Select.js`, `notched: true`), es steht hier nur
            ausdrücklich, damit die Absicht im Aufruf lesbar bleibt.
            🔴 bewusst REDUNDANT, gemessen 2026-10-08 (R15 K3): nur `notched`
            entfernt → Kerben-Probe gruen; nur `displayEmpty` entfernt → gruen;
            BEIDE entfernt → rot. Die Probe
            (`BottomSheetMultiSelect.etikett.test.jsx`) sichert den ENDZUSTAND
            (der Rahmen ist gekerbt), nicht diese einzelne Angabe. */}
        <InputLabel id={selectLabelId} shrink>{label}</InputLabel>
        <Select
          labelId={selectLabelId}
          multiple
          displayEmpty
          notched
          label={label}
          value={value}
          onChange={(e) => {
            const next =
              typeof e.target.value === 'string'
                ? e.target.value.split(',')
                : e.target.value;
            onChange(next);
          }}
          input={<OutlinedInput label={label} />}
          // 🔴 Der Wert steht als TEXT im Feld, nicht als Pille
          // (E1a Folgerunde 9, 2026-10-08).
          //
          // Der Befund, gemessen im Foto: das Feld mit Werten war HOEHER als
          // seine Nachbarn in derselben Reihe — `/products` @1440: Status mit
          // „active, draft" ~47 px gegen Suche/Category/Product type ~36 px;
          // Fulfillment: „Months" mit „Jul 2026" ~44 px gegen „Carriers"/
          // „Country" ~36 px. Die Ursache ist die PILLE: sie ist ~26 px hoch
          // (`py: 0.5` plus die Zeilenhoehe von `FS.sm`) und passt nicht in den
          // Inhalt eines `size="small"`-Feldes — sie treibt die Feldhoehe mit.
          //
          // Die Entscheidung (Orchestrator): F2 „eine Hoehe je Zeile" schlaegt
          // die Pillenform. Eine Zeile Text — die Etiketten der gewaehlten
          // Optionen, kommagetrennt — hat dieselbe Inhaltshoehe wie der Wert
          // eines Einfachauswahl-Feldes und damit dieselbe Feldhoehe.
          //
          // 🔴 Die Regel-16-W1-Notiz (EIN Chip-Bauteil, Regel 22 §1) bleibt
          // gewahrt, und zwar strenger als vorher: im Feld steht jetzt GAR KEIN
          // Chip, statt eines zweiten. Die Pille bleibt im mobilen Blatt
          // (`FilterSheet`), wo sie keine Feldhoehe traegt.
          //
          // 🔴 `lineHeight: 1.4375` ist KEIN Geschmackswert: MUI's
          // `MuiInputBase-input` traegt `height: 1.4375em` bei
          // `box-sizing: content-box` — das ist die Inhaltshoehe des
          // Einfachauswahl-Feldes daneben. Ohne die Zeile braechte der Text seine
          // eigene Zeilenhoehe mit (1.5 × 14 px = 21 px) und stuende 0,9 px
          // ueber dem Nachbarfeld; die Zusage lautet „Spanne ≤ 1 px", und ein
          // Wert innerhalb der Messungenauigkeit ist kein Beleg (Regel 17 §1).
          //
          // `noWrap`-Geometrie von Hand statt ueber `Typography`: `noWrap` ist
          // eine `Typography`-Eigenschaft und landete an einer `Box` als
          // DOM-Attribut. `minWidth: 0` + `maxWidth: '100%'` halten die
          // Auslassung auch dann, wenn MUI den Anzeige-Kasten als Flex-Kasten
          // zeichnet (dort schrumpft ein Kind sonst nicht unter seine
          // Inhaltsbreite).
          renderValue={(selected) => {
            // Leer ⇒ NICHTS zeichnen: ein leeres Feld soll leer aussehen. (Die
            // Etikettenlage ist hier NICHT Gegenstand — sie wird durch `shrink` am
            // `InputLabel` dieses Bausteins geregelt, siehe oben.)
            if (!Array.isArray(selected) || selected.length === 0) return null;
            const text = selected
              .map((val) => options.find((o) => o.value === val)?.label ?? val)
              .join(', ');
            return (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  minWidth: 0,
                  maxWidth: '100%',
                  lineHeight: 1.4375,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {text}
              </Box>
            );
          }}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Checkbox checked={value.includes(opt.value)} size="small" sx={{ mr: 0.5, p: 0 }} />
              <ListItemText primary={optionText(opt)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // ── Mobile: glass pill ─────────────────────────────────────────
  const hasValue = value.length > 0;
  // 🔴 N11.8: mobil traegt der Knopf die ZAHL der gesetzten Kriterien, nicht
  // die Werte („Status (2)") — Facetten stehen mobil nie inline, sie liegen im
  // Blatt (Konzept §11 N11.8).
  const triggerLabel = hasValue ? `${label} (${value.length})` : label;
  // 🔴 R16-Blocker 1 / R15-W5: NUR in der Facettenform wird der Ausloeser ein
  // richtiger Knopf. `alsKnopf` traegt `role`, `tabIndex`, `onKeyDown` und
  // `aria-expanded` — ohne `desktopForm="pill"` bleibt der Ausloeser Zeichen
  // fuer Zeichen der heutige (Vorgabe, additiv).
  const alsKnopf = !disabled && desktopForm === 'pill';

  const toggle = (val) => {
    const next = value.includes(val)
      ? value.filter((v) => v !== val)
      : [...value, val];
    onChange(next);
  };

  return (
    <>
      <Box
        onClick={disabled ? undefined : () => setOpen(true)}
        // 🔴 R16-Blocker 1 / R15-W5 (2026-10-12): in der Facettenform wird der
        // Ausloeser ein RICHTIGER Knopf. Die erste Fassung setzte nur `role`
        // und `aria-haspopup` — ein Element, das sich als Knopf ausgibt, aber
        // NICHT in der Tab-Reihenfolge steht und auf Enter/Leertaste nicht
        // reagiert (WCAG 2.1.1, Stufe A). Genau der Defekt, den
        // `SearchTrigger.jsx:244-252` am 2026-10-08 beschrieben und behoben
        // hat; `BottomSheetSelect` traegt dieselbe Hausform seit Welle 3 PR 3.
        role={alsKnopf ? 'button' : undefined}
        tabIndex={alsKnopf ? 0 : undefined}
        onKeyDown={alsKnopf ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        } : undefined}
        aria-expanded={alsKnopf ? open : undefined}
        // 🔴 R16-W1 / R15-W4: HIER immer `"dialog"` — die Flaeche ist das
        // Blatt, und das ist `role="dialog"`. Die erste Fassung sagte in der
        // Facettenform `"listbox"` an und versprach eine Liste, die nicht kommt.
        // 🔴 Und zwar am SELBEN Waechter wie `role`/`tabIndex`/`aria-expanded`
        // (`alsKnopf`): bis zur Wiedervorlage 2 hing die Zusage an
        // `desktopForm === 'pill'` allein, sagte also bei `disabled` eine
        // Flaeche an, deren Rolle fehlte (R15-W2 / R16-W4). Fuer jeden
        // Bestandsaufrufer ist die Zeichenkette Zeichen fuer Zeichen dieselbe:
        // ohne `desktopForm="pill"` ist `alsKnopf` falsch, also `undefined` —
        // genau wie `desktopForm === 'pill'` es ergab.
        aria-haspopup={alsKnopf ? 'dialog' : undefined}
        sx={{
          ...GLASS_PILL_SX,
          gap: 0.5,
          color: hasValue ? 'primary.main' : 'text.primary',
          borderColor: hasValue ? 'primary.main' : 'var(--glass-border)',
          fontWeight: hasValue ? 600 : 500,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'default' : 'pointer',
          ...sx,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 'inherit', lineHeight: 1, whiteSpace: 'nowrap' }}>
          {triggerLabel}
        </Typography>
      </Box>

      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disableSwipeToOpen
        {...BOTTOM_SHEET_TRANSITION}
        PaperProps={{ sx: { borderRadius: '16px 16px 0 0', maxHeight: '80vh' } }}
      >
        <Box sx={{ pt: 2, pb: 3 }}>
          {/* Handle bar */}
          <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: 'divider', mx: 'auto', mb: 2 }} />

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <List disablePadding sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
            {options.map((opt) => {
              const checked = value.includes(opt.value);
              return (
                <ListItemButton
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  sx={{ mx: 1, borderRadius: 1, py: 0.75 }}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    disableRipple
                    sx={{ mr: 1, p: 0 }}
                  />
                  <ListItemText
                    primary={optionText(opt)}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Collapse in={hasValue}>
            <Divider sx={{ mt: 1 }} />
            <Box sx={{ px: 2, pt: 1.5 }}>
              <Button fullWidth size="small" onClick={() => { onChange([]); setOpen(false); }}>
                Clear all
              </Button>
            </Box>
          </Collapse>
        </Box>
      </SwipeableDrawer>
    </>
  );
}
